import { roundToCents } from "@common/helpers/monetary";
import {
    AccrualMatchDisplay,
    AccrualMatchLineItemBreakdown,
} from "@common/schemas/domains/accrualReview/accrual-review.schema";
import { MatchableItem, MatchableLineItem } from "../types";
import {
    AllocationTotals,
    buildInitialPaymentAmounts,
    lineItemToMatchableItem,
} from "./allocationHelpers";

export type LinePayments = Record<string, number>;
export type PaymentsByTransaction = Record<number, LinePayments>;

export function sumPaymentsExcluding(
    paymentsByTransaction: PaymentsByTransaction,
    excludedTransactionId: number | null,
): LinePayments {
    const totals: LinePayments = {};
    for (const [transactionId, payments] of Object.entries(
        paymentsByTransaction,
    )) {
        if (Number(transactionId) === excludedTransactionId) {
            continue;
        }
        for (const [lineItemId, amount] of Object.entries(payments)) {
            totals[lineItemId] = roundToCents(
                (totals[lineItemId] ?? 0) + amount,
            );
        }
    }
    return totals;
}

function withLineOpenAmounts(
    document: MatchableItem,
    getOpenAmount: (lineItem: MatchableLineItem) => number,
): MatchableItem {
    return {
        ...document,
        lineItems: document.lineItems?.map((lineItem) => ({
            ...lineItem,
            openAmount: getOpenAmount(lineItem),
        })),
    };
}

export function shrinkDocumentOpenAmounts(
    document: MatchableItem,
    usedByOthers: LinePayments,
): MatchableItem {
    return withLineOpenAmounts(document, (lineItem) =>
        Math.max(
            0,
            roundToCents(
                lineItem.openAmount - (usedByOthers[lineItem.id] ?? 0),
            ),
        ),
    );
}

export function restoreDocumentOpenAmounts(
    document: MatchableItem,
    persistedPaymentsByTransaction: PaymentsByTransaction,
): MatchableItem {
    const persisted = sumPaymentsExcluding(
        persistedPaymentsByTransaction,
        null,
    );
    return withLineOpenAmounts(document, (lineItem) =>
        roundToCents(lineItem.openAmount + (persisted[lineItem.id] ?? 0)),
    );
}

export function collectPersistedLinePayments({
    document,
    accrualMatches,
}: {
    document: MatchableItem;
    accrualMatches: ReadonlyArray<{
        lineItems?: AccrualMatchLineItemBreakdown[];
    }>;
}): LinePayments {
    const documentLineIds = new Set(
        (document.lineItems ?? []).map((lineItem) => lineItem.id),
    );
    const payments: LinePayments = {};
    for (const { lineItemId, amount } of accrualMatches.flatMap(
        (match) => match.lineItems ?? [],
    )) {
        if (documentLineIds.has(lineItemId)) {
            payments[lineItemId] = roundToCents(
                (payments[lineItemId] ?? 0) + amount,
            );
        }
    }
    return payments;
}

export function hasMatchesOutsideDocument({
    documentId,
    accrualMatches,
}: {
    documentId: string;
    accrualMatches: ReadonlyArray<
        Pick<AccrualMatchDisplay, "invoice" | "bill">
    >;
}): boolean {
    return accrualMatches.some(
        (match) => (match.invoice ?? match.bill)?.id !== documentId,
    );
}

export function buildSequentialPayments({
    document,
    transactions,
    persistedPaymentsByTransaction,
}: {
    document: MatchableItem;
    transactions: ReadonlyArray<{ id: number; amount: number }>;
    persistedPaymentsByTransaction: PaymentsByTransaction;
}): PaymentsByTransaction {
    const result: PaymentsByTransaction = {
        ...persistedPaymentsByTransaction,
    };
    for (const transaction of transactions) {
        const persisted = persistedPaymentsByTransaction[transaction.id] ?? {};
        const shrunk = shrinkDocumentOpenAmounts(
            document,
            sumPaymentsExcluding(result, transaction.id),
        );
        result[transaction.id] = buildInitialPaymentAmounts(
            [shrunk],
            transaction.amount,
            new Map(Object.entries(persisted)),
        );
    }
    return result;
}

export function hasOverAllocatedLines(
    document: MatchableItem,
    paymentsByTransaction: PaymentsByTransaction,
): boolean {
    const totals = sumPaymentsExcluding(paymentsByTransaction, null);
    return (document.lineItems ?? []).some(
        (lineItem) => (totals[lineItem.id] ?? 0) > lineItem.openAmount,
    );
}

export function sumLinePayments(values: LinePayments | undefined): number {
    return roundToCents(
        Object.values(values ?? {}).reduce((sum, amount) => sum + amount, 0),
    );
}

export interface TransactionAllocationRow {
    transactionId: number;
    amount: number;
    linesTotal: number;
    allocated: number;
}

export function buildMultiAllocationTotals({
    transactions,
    paymentsByTransaction,
    remainingTotalsByTransaction,
}: {
    transactions: ReadonlyArray<{ id: number; amount: number }>;
    paymentsByTransaction: PaymentsByTransaction;
    remainingTotalsByTransaction: Record<number, number>;
}): { rows: TransactionAllocationRow[]; totals: AllocationTotals } {
    const rows = transactions.map((transaction) => {
        const linesTotal = sumLinePayments(
            paymentsByTransaction[transaction.id],
        );
        return {
            transactionId: transaction.id,
            amount: Math.abs(transaction.amount),
            linesTotal,
            allocated: roundToCents(
                linesTotal +
                    (remainingTotalsByTransaction[transaction.id] ?? 0),
            ),
        };
    });
    const amountToBeAllocated = roundToCents(
        rows.reduce((sum, row) => sum + row.amount, 0),
    );
    const allocatedTotal = roundToCents(
        rows.reduce((sum, row) => sum + row.allocated, 0),
    );
    return {
        rows,
        totals: {
            transactionItemsTotal: 0,
            amountToBeAllocated,
            allocatedTotal,
            remainingAmount: roundToCents(amountToBeAllocated - allocatedTotal),
        },
    };
}

const ROW_KEY_SEPARATOR = "::";

export function toPaymentRowKey(
    transactionId: number,
    lineItemId: string,
): string {
    return `${transactionId}${ROW_KEY_SEPARATOR}${lineItemId}`;
}

export function parsePaymentRowKey(
    rowKey: string,
): { transactionId: number; lineItemId: string } | null {
    const separatorIndex = rowKey.indexOf(ROW_KEY_SEPARATOR);
    if (separatorIndex < 0) {
        return null;
    }
    const transactionId = Number(rowKey.slice(0, separatorIndex));
    if (!Number.isFinite(transactionId)) {
        return null;
    }
    return {
        transactionId,
        lineItemId: rowKey.slice(separatorIndex + ROW_KEY_SEPARATOR.length),
    };
}

export function flattenPayments(
    paymentsByTransaction: PaymentsByTransaction,
): Record<string, number> {
    return Object.fromEntries(
        Object.entries(paymentsByTransaction).flatMap(
            ([transactionId, payments]) =>
                Object.entries(payments).map(([lineItemId, amount]) => [
                    toPaymentRowKey(Number(transactionId), lineItemId),
                    amount,
                ]),
        ),
    );
}

export function buildSelectedRows({
    document,
    transactionItems,
    paymentsByTransaction,
}: {
    document: MatchableItem;
    transactionItems: ReadonlyArray<{ id: number; item: MatchableItem }>;
    paymentsByTransaction: PaymentsByTransaction;
}): MatchableItem[] {
    return transactionItems.map(({ id, item }) => {
        const net = shrinkDocumentOpenAmounts(
            document,
            sumPaymentsExcluding(paymentsByTransaction, id),
        );
        return {
            ...item,
            children: (net.lineItems ?? []).map((lineItem) =>
                lineItemToMatchableItem(
                    { ...lineItem, id: toPaymentRowKey(id, lineItem.id) },
                    document,
                ),
            ),
        };
    });
}
